import React from 'react';
import * as yup from 'yup';
import { Avatar, Divider, Grid } from '@mui/material';
import { SuccessStatusIcon, WarningStatusIcon } from 'components/SVG';
import {
  DynamicUIComponentFieldController,
  SparkForm,
  SparkFormButton,
} from 'sharedComponents/SparkForm';
import FieldSkeleton from 'sharedComponents/Skeleton/FieldSkeleton';
import { SparkAccordion } from 'sharedComponents/UI';
import {
  useStepParametersByStepID,
  useUpdateStepParameter,
} from 'hooks/SparkBuilder/parameters/stepParameter';
import { usePluginFunctionParameters } from 'hooks/SparkBuilder/parameters';
import { useSparkBuilderStore } from 'stores';
import { evaluateInitialValue } from 'utilities/form';
import { parseJSONCleaned } from 'utilities/index';
import type { DialogConfig } from 'hooks/common/useDialog';
import type { DropdownOption } from 'types/common/DropdownOption';
import type { EnumValues, SliderOptions, SliderValues } from 'types/common';
import type { PluginConfigFormValues } from './types';

const getIsMultiSelectField = (enumValue: string | undefined | null) => {
  if (!enumValue) {
    return false;
  }
  const parsedEnumValues = parseJSONCleaned<EnumValues>(enumValue);

  return parsedEnumValues?.selectionType === 'multi';
};

type Props = {
  dialogConfig: DialogConfig | undefined;
  pluginFunctionID: string | null;
  handleConfigSubmit: (formValues: PluginConfigFormValues) => void;
  isLoadingConfigData: boolean;
  isMutating: boolean;
};

function PluginConfigForm({
  dialogConfig,
  pluginFunctionID,
  handleConfigSubmit,
  isLoadingConfigData,
  isMutating,
}: Props) {
  const { sparkStep } = useSparkBuilderStore((store) => ({
    sparkStep: store.sparkStepsActions.getSparkStep(
      store.builderSideBar.selectedStep?.id || ''
    ),
  }));

  const { stepParameters, parameters } = useStepParametersByStepID(
    sparkStep?.id
  );

  const { data: { data: pluginFunctionParameters } = {} } =
    usePluginFunctionParameters(pluginFunctionID);
  const { mutateAsync: updateSparkStepParameter } = useUpdateStepParameter(
    sparkStep?.id
  );

  const initialConfigFormValue: PluginConfigFormValues = React.useMemo(() => {
    return (
      stepParameters?.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.id + '']: evaluateInitialValue(curr.attributes.value),
        };
      }, {}) || {}
    );
  }, [stepParameters]);

  const validationSchema = yup.object().shape(
    stepParameters?.reduce((acc, curr) => {
      const parameter = parameters.find(
        (parameter) => parameter.id === curr.attributes.parameterID
      );
      const { attributes: parameterAttributes } = parameter || {};
      const isMultiSelectField = getIsMultiSelectField(
        parameterAttributes?.enumValues
      );
      if (isMultiSelectField) {
        return {
          ...acc,
          [curr.id]: parameterAttributes?.required
            ? yup
                .array(yup.string())
                .min(1)
                .required(`${parameterAttributes?.headerText} is required`)
            : yup
                .array(yup.string())
                .typeError(
                  `${parameterAttributes?.headerText} should be string`
                ),
        };
      }
      return {
        ...acc,
        [curr.id]: parameterAttributes?.required
          ? yup
              .string()
              .required(`${parameterAttributes?.headerText} is required`)
          : yup
              .string()
              .typeError(`${parameterAttributes?.headerText} should be string`),
      };
    }, {}) || {}
  );
  const stepMetaDiagnostic = sparkStep?.meta.diagnostics;
  const onFieldSwitch = (parameterID: string, mapped: boolean) => {
    updateSparkStepParameter({ stepParameterID: parameterID, map: mapped });
  };
  return (
    <>
      <SparkAccordion
        title={`Config `}
        sx={{ border: 'none' }}
        dialogConfig={dialogConfig}
        accordionTitleProps={{ variant: 'h5' }}
        summaryStyles={{ paddingX: 2, paddingY: 1, cursor: 'pointer' }}
        detailsStyle={{ paddingBottom: 2 }}
        icon={
          <Avatar
            sx={{
              width: '30px',
              height: '30px',
            }}
          >
            {stepMetaDiagnostic?.hasPluginConfigIssues ? (
              <WarningStatusIcon width='100%' height='100%' />
            ) : (
              <SuccessStatusIcon width='100%' height='100%' />
            )}
          </Avatar>
        }
      >
        <SparkForm
          validationSchema={validationSchema}
          initialValues={initialConfigFormValue}
          onSave={handleConfigSubmit}
          enableReinitialize={true}
          sx={{ paddingX: 2, paddingY: 1.5 }}
        >
          {isLoadingConfigData ? (
            <Grid container direction='column'>
              <FieldSkeleton count={3} />
            </Grid>
          ) : (
            <>
              {stepParameters?.map((StepParameter) => {
                const pluginFunctionParameter = pluginFunctionParameters?.find(
                  (functionParameter) =>
                    functionParameter.attributes.parameterID ===
                    StepParameter.attributes.parameterID
                );
                const parameter = parameters.find(
                  (parameter) =>
                    parameter.id === StepParameter.attributes.parameterID
                );
                if (!parameter) {
                  return null;
                }
                const { attributes: parameterAttributes } = parameter || {};

                const {
                  enumValues,
                  placeholderText,
                  uiComponentID,
                  subHeaderText,
                  headerText,
                  helpText,
                  sliderValues,
                } = parameterAttributes || {};

                const optionEnumValues = parseJSONCleaned<EnumValues>(
                  enumValues || ''
                );
                const parsedSliderValues = parseJSONCleaned<SliderValues>(
                  sliderValues || ''
                );
                const sliderOptions = parsedSliderValues?.sliderOptions;

                const _sliderOptions: SliderOptions = {
                  min: Number(sliderOptions?.min),
                  max: Number(sliderOptions?.max),
                  step: Number(sliderOptions?.stepSize),
                  decimalPrecision: Number(sliderOptions?.decimalPrecision),
                  renderPercentage: !!sliderOptions?.renderPercentage,
                };
                const options: DropdownOption[] =
                  optionEnumValues?.options.map((option) => ({
                    value: option.value,
                    label: option.title,
                    iconUrl: option.iconURL,
                    description: option.description,
                  })) || [];

                return (
                  <DynamicUIComponentFieldController
                    name={StepParameter.id}
                    helpText={helpText}
                    label={headerText}
                    placeholder={placeholderText}
                    subHeaderText={subHeaderText}
                    uiComponentID={uiComponentID}
                    sliderOptions={_sliderOptions}
                    key={parameter.id}
                    options={options}
                    isFieldMapped={StepParameter.attributes.map}
                    isFieldMappable={
                      pluginFunctionParameter?.attributes.mappable
                    }
                    forceFieldMappable={true}
                    onFieldSwitch={(mapped) => {
                      onFieldSwitch(StepParameter.id, mapped);
                    }}
                  />
                );
              })}
              <SparkFormButton
                sx={{
                  alignSelf: 'flex-end',
                  paddingY: 1.5,
                  paddingX: 3,
                }}
                disabled={isLoadingConfigData || isMutating}
                disabledMessage={
                  isLoadingConfigData || isMutating
                    ? 'Continue'
                    : 'Complete the required fields'
                }
              >
                Continue
              </SparkFormButton>
            </>
          )}
        </SparkForm>
      </SparkAccordion>
      <Divider />
    </>
  );
}

export default PluginConfigForm;
